import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  combinedMachines,
  fabricationMachines,
  printingMachines,
} from "../enums/vendorMachines";
import {
  Grid,
  MenuItem,
  FormControl,
  Select,
  Radio,
  InputLabel,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import Decrypt from "../utils/Decrypt";
import VendorDialog from "../../components/AdminportalFolder/VendorDetails/vendorModal";
import GlobalSnackbar from "../utils/GlobalSnackbar";
import CircularProgress from "@mui/material/CircularProgress";

const normalizeCity = (name = "") => name?.toLowerCase().trim();

// Convert adminApproval value to status text
const getAdminApprovalStatus = (adminApproval) => {
  if (adminApproval === 1) return "Approved";
  if (adminApproval === -2) return "Blacklisted";
  if (adminApproval === -1) return "Rejected";
  return "Pending"; // 0 or anything else
};

const getStatusBadge = (adminApproval) => {
  const base = "text-xs px-3 py-1 rounded-full font-semibold";
  switch (adminApproval) {
    case 1: // Approved
      return `${base} bg-green-100 text-green-700`;
    case -2: // Blacklisted
      return `${base} bg-red-100 text-red-700`;
    case -1: // Rejected
      return `${base} bg-yellow-100 text-yellow-700`;
    default: // Pending (0 or anything else)
      return `${base} bg-gray-200 text-gray-600`;
  }
};

const CityAccordion = ({
  data,
  showDropdown = true,
  showVendors = true,
  productType,
  onTotalAreaChange,
  formData,
  externalVendorsList = null,
  approvedOnly = false,
}) => {
  const [cityWiseData, setCityWiseData] = useState([]);
  const [originalCityWiseData, setOriginalCityWiseData] = useState([]);
  const serverURL = process.env.REACT_APP_URL;
  const [loading, setLoading] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  const [openCity, setOpenCity] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const [selectedMachinesMap, setSelectedMachinesMap] = useState({});
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [selectedVendorData, setSelectedVendorData] = useState({});
  const role = localStorage.getItem("role");

  // Add ref to track if already fetched
  const hasFetchedRef = useRef(false);
  const isProcessingRef = useRef(false);

  const handleChange = (event, cityName) => {
    const {
      target: { value },
    } = event;

    const selectedValues = typeof value === "string" ? value.split(",") : value;

    const updatedMap = {
      ...selectedMachinesMap,
      [cityName]: selectedValues,
    };
    setSelectedMachinesMap(updatedMap);

    filterVendorsForCity(cityName, selectedValues);
  };

  const processVendorsData = async (vendorsData) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    setLoading(true);
    try {
      const cityData = getCityDataArray(data, productType);

      //  FILTER BY APPROVAL STATUS IF NEEDED
      let filteredVendorsData = vendorsData;
      if (approvedOnly && vendorsData) {
        filteredVendorsData = vendorsData.filter(v => v.adminApproval === 1);
      }

      const vendorsByCity = filteredVendorsData.reduce((acc, vendor) => {
        const city = normalizeCity(vendor.city || "");
        if (!acc[city]) acc[city] = [];
        acc[city].push(vendor);
        return acc;
      }, {});

      const enrichedResult = await Promise.all(
        cityData.map(async (cityObj) => {
          const cityName = normalizeCity(cityObj.name);
          const locations = cityObj.locations || [];
          const vendors = vendorsByCity[cityName] || [];

          const centroid = calculateCentroid(locations);
          const c = normalizeCoord(centroid);

          let finalVendors = [];

          // ✅ CASE 1: Vendors already in same city
          if (vendors.length > 0) {
            finalVendors = vendors;
          } else if (filteredVendorsData.length > 0 && c) {
            finalVendors = filteredVendorsData
              .map((v) => {
                const vC = normalizeCoord(v.locationCoordinates);
                if (!c || !vC) return null;

                return {
                  ...v,
                  distanceFromCentroid: getDistance(
                    c.latitude,
                    c.longitude,
                    vC.latitude,
                    vC.longitude,
                  ),
                };
              })
              .filter(Boolean) // ❗ NaN / invalid remove
              .sort((a, b) => a.distanceFromCentroid - b.distanceFromCentroid)
              .slice(0, 100); // 🔒 nearest decision ONLY here
          }
          // 🔹 TSP only for route optimization
          finalVendors = await Promise.all(
            finalVendors.map(async (vendor) => {
              const tsp = geneticTSP(vendor, locations);
              return {
                ...vendor,
                totalDistanceKm: tsp.totalDistanceKm * 1.3,
                optimizedPath: tsp.optimizedPath,
              };
            }),
          );
          // ✅ Final sort by route distance (UI purpose)
          finalVendors.sort(
            (a, b) => (a.totalDistanceKm || 0) - (b.totalDistanceKm || 0),
          );

          return {
            ...cityObj,
            noVendorsInCity: vendors.length === 0,
            // vendors: finalVendors,
            vendors:
              parseInt(role) >= 0 ? finalVendors.slice(0, 10) : finalVendors,
          };
        }),
      );

      setCityWiseData(enrichedResult);
      setOriginalCityWiseData(JSON.parse(JSON.stringify(enrichedResult)));
    } catch (error) {
      console.error("Error processing vendors data:", error);
    } finally {
      setLoading(false);
      isProcessingRef.current = false;
    }
  };

  // Main useEffect - process external vendors when they become available
  useEffect(() => {
    if (!data) return;

    if (!Array.isArray(externalVendorsList)) {
      setLoading(true);
      return;
    }

    if (externalVendorsList.length > 0 && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      processVendorsData(externalVendorsList);
    } else if (externalVendorsList.length === 0) {
      setLoading(false);
      setCityWiseData([]);
    }
  }, [data, productType, externalVendorsList, approvedOnly]); // ✅ ADD approvedOnly to dependency

  const filterVendorsForCity = async (cityName, selectedMachineValues) => {
    if (!selectedMachineValues || selectedMachineValues.length === 0) {
      if (originalCityWiseData.length > 0) {
        setCityWiseData(originalCityWiseData);
      } else if (externalVendorsList && externalVendorsList.length > 0) {
        await processVendorsData(externalVendorsList);
      }
      return;
    }

    setIsFiltering(true);
    setLoading(true);

    try {
      const fabricationValues = fabricationMachines.map((m) => m.value.trim());
      const fabrication = selectedMachineValues.filter((m) =>
        fabricationValues.includes(m.trim()),
      );
      const printing = selectedMachineValues.filter(
        (m) => !fabricationValues.includes(m.trim()),
      );

      //  START WITH EXTERNAL VENDORS LIST
      let filteredVendors = [...externalVendorsList];

      //  FILTER BY APPROVAL STATUS IF NEEDED
      if (approvedOnly) {
        filteredVendors = filteredVendors.filter(v => v.adminApproval === 1);
      }

      //  FILTER BY FABRICATION MACHINES (CLIENTSIDE)
      if (fabrication.length > 0) {
        filteredVendors = filteredVendors.filter(vendor => {
          const vendorFabMachines = vendor.fabricationMachines || [];
          return fabrication.some(fabMachine =>
            vendorFabMachines.includes(fabMachine)
          );
        });
      }

      //  FILTER BY PRINTING MACHINES (CLIENTSIDE)
      if (printing.length > 0) {
        filteredVendors = filteredVendors.filter(vendor => {
          const vendorPrintMachines = vendor.printingMachines || [];
          return printing.some(printMachine =>
            vendorPrintMachines.includes(printMachine)
          );
        });
      }

      if (filteredVendors.length === 0) {
        setCityWiseData((prev) =>
          prev.map((city) =>
            normalizeCity(city.name) === normalizeCity(cityName)
              ? { ...city, vendors: [], noVendorsInCity: true }
              : city,
          ),
        );
        setLoading(false);
        setIsFiltering(false);
        return;
      }

      const cityObj = originalCityWiseData.find(
        (c) => normalizeCity(c.name) === normalizeCity(cityName),
      );

      if (cityObj && cityObj.locations && cityObj.locations.length > 0) {
        const centroid = calculateCentroid(cityObj.locations);
        const c = normalizeCoord(centroid);

        if (c) {
          const vendorsWithDistance = await Promise.all(
            filteredVendors.map(async (vendor) => {
              const vC = normalizeCoord(vendor.locationCoordinates);
              let distance = 0;
              if (c && vC) {
                distance = getDistance(c.latitude, c.longitude, vC.latitude, vC.longitude);
              }
              const tspResult = geneticTSP(vendor, cityObj.locations);
              return {
                ...vendor,
                distanceFromCentroid: distance,
                totalDistanceKm: tspResult.totalDistanceKm * 1.3,
                optimizedPath: tspResult.optimizedPath,
              };
            }),
          );

          vendorsWithDistance.sort((a, b) => a.totalDistanceKm - b.totalDistanceKm);

          setCityWiseData((prev) =>
            prev.map((city) =>
              normalizeCity(city.name) === normalizeCity(cityName)
                ? {
                  ...city,
                  vendors: parseInt(role) >= 0 ? vendorsWithDistance.slice(0, 10) : vendorsWithDistance,
                  noVendorsInCity: false,
                }
                : city,
            ),
          );
        } else {
          setCityWiseData((prev) =>
            prev.map((city) =>
              normalizeCity(city.name) === normalizeCity(cityName)
                ? {
                  ...city,
                  vendors: parseInt(role) >= 0 ? filteredVendors.slice(0, 10) : filteredVendors,
                  noVendorsInCity: false,
                }
                : city,
            ),
          );
        }
      } else {
        setCityWiseData((prev) =>
          prev.map((city) =>
            normalizeCity(city.name) === normalizeCity(cityName)
              ? {
                ...city,
                vendors: parseInt(role) >= 0 ? filteredVendors.slice(0, 10) : filteredVendors,
                noVendorsInCity: false,
              }
              : city,
          ),
        );
      }
    } catch (error) {
      console.error("Error filtering vendors for city:", error);
      setSnackbar({
        open: true,
        message: "Error filtering vendors. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
      setIsFiltering(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleVendorView = (v) => {
    let id = "";
    if (role !== "-1" && role !== "3" && role !== "0") {
      return;
    }
    id = v.id || v._id;

    axios
      .get(`${serverURL}/api/vendors/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          let vendorData = {
            ...response.data,
            GSTIN: Decrypt(response.data.GSTIN),
            ownerName: Decrypt(response.data.ownerName),
            ownerPrimaryContactNo: Decrypt(response.data.ownerPrimaryContactNo),
            ownerSecondaryContactNo: Decrypt(
              response.data.ownerSecondaryContactNo,
            ),
          };

          setSelectedVendorData(vendorData);

          setIsVendorModalOpen(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching vendors:", error);
      });
  };

  const toggleCity = (index) => {
    setOpenCity(openCity === index ? null : index);
  };

  const accToProductArray = cityWiseData?.filter((ele) => {
    if (!productType?.length) return true;
    if (productType?.includes("All")) return true;
    return ele?.locations?.some((loc) =>
      productType?.includes(loc?.product?.productType),
    );
  });

  if (loading || isFiltering) {
    return (
      <div className="flex justify-center items-center w-full py-8">
        <CircularProgress size={32} />
        <span className="ml-2">{isFiltering ? "Filtering vendors..." : "Loading vendors..."}</span>
      </div>
    );
  }

  if (!accToProductArray?.length) {
    return (
      <div className="text-center text-gray-500 py-4">
        No data found for city.
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-md max-h-[34rem] overflow-y-auto">
      {accToProductArray?.map((city, index) => {
        const isOpen = openCity === index;
        return (
          <div
            key={index}
            className={`bg-white rounded-xl shadow-md p-4 transition-shadow duration-500 space-y-2 ${isOpen ? "shadow-lg ring-1 ring-blue-200" : ""
              }`}
          >
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => toggleCity(index)}
            >
              <div className="text-black-700 font-semibold">{`${city.name
                } : ${city.totalAreaSqFt?.toFixed(2) || 0} sqft`}</div>
              <div
                className={`flex items-center gap-2 text-sm text-gray-500 ${showDropdown ? "justify-between w-64" : ""
                  }`}
              >
                {showDropdown && (
                  <FormControl fullWidth size="small">
                    <Select
                      id={`machines-select-${city.name}`}
                      multiple
                      value={selectedMachinesMap[city.name] || []}
                      onChange={(e) => handleChange(e, city.name)}
                      renderValue={(selected) =>
                        selected.length > 0
                          ? selected.join(", ")
                          : "Select Machines"
                      }
                      displayEmpty
                    >
                      {combinedMachines.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Checkbox
                            checked={
                              selectedMachinesMap[city.name]?.includes(
                                option.value,
                              ) || false
                            }
                          />
                          <ListItemText primary={option.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <div className="flex items-center">
                  <span className="w-max">{city.sites} sites</span>
                  <span
                    className={`text-blue-600 text-lg ml-4 transform transition-transform duration-300 ${isOpen ? "rotate-90" : ""
                      }`}
                  >
                    &gt;
                  </span>
                </div>
              </div>
            </div>

            {/* Accordion Content */}
            {showVendors && (
              <div
                className={`transition-all duration-500 ease-in-out px-2 overflow-hidden ${isOpen
                  ? "max-h-[1000px] opacity-100 pt-4 pointer-events-auto"
                  : "max-h-0 opacity-0 pointer-events-none"
                  }`}
              >
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: 300,
                    boxShadow: "none",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <Table stickyHeader size="small">
                    {/* 🔹 HEADER */}
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 600, width: 40, color: "gray" }}
                        >
                          S.No
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: 600, width: 100, color: "gray" }}
                        >
                          Vendor Name
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: 600, width: 360, color: "gray" }}
                        >
                          Address
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: 600, width: 160, color: "gray" }}
                        >
                          City
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: 600, width: 160, color: "gray" }}
                        >
                          State
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: 600, width: 120, color: "gray" }}
                        >
                          Admin Approval
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: 600, width: 250, color: "gray" }}
                        >
                          Distance (Covering All Locations)
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    {/* 🔹 BODY */}
                    <TableBody>
                      {city.noVendorsInCity && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <div className="bg-yellow-100 text-yellow-700 text-sm py-2 rounded">
                              No vendors found with the selected criteria in {city.name}.
                            </div>
                          </TableCell>
                        </TableRow>
                      )}

                      {city?.vendors?.map((vendor, id) => (
                        <TableRow
                          key={vendor._id || vendor.id || id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleVendorView(vendor)}
                        >
                          <TableCell>{vendor?.code || `${id + 1}`}</TableCell>

                          <TableCell align="center">
                            {vendor?.vendorBusinessName}
                          </TableCell>

                          <TableCell align="center">
                            {`${vendor?.address} - ${vendor?.pinCode}`}
                          </TableCell>
                          <TableCell align="center">{vendor?.city}</TableCell>
                          <TableCell align="center">{vendor?.state}</TableCell>

                          <TableCell align="center">
                            <span
                              className={`px-2 py-1 rounded-md ${getStatusBadge(
                                vendor?.adminApproval,
                              )}`}
                            >
                              {getAdminApprovalStatus(vendor?.adminApproval)}
                            </span>
                          </TableCell>

                          <TableCell align="center">
                            {vendor?.totalDistanceKm?.toFixed(2) || "00.00"} km
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <GlobalSnackbar
                  snackbar={snackbar}
                  handleClose={handleSnackbarClose}
                />
              </div>
            )}
          </div>
        );
      })}
      {isVendorModalOpen && (
        <VendorDialog
          isVendorModalOpen={isVendorModalOpen}
          setIsVendorModalOpen={setIsVendorModalOpen}
          selectedVendorData={selectedVendorData}
          setSelectedVendorData={setSelectedVendorData}
          setSnackbar={setSnackbar}
          setAnchorEl={setAnchorEl}
          snackbar={snackbar}
        />
      )}
    </div>
  );
};

export default CityAccordion;

//////---- count city locations---///
const getCityDataArray = (data, productType) => {
  if (!data || !Array.isArray(data.locations)) return [];

  const cityMap = {};

  data.locations.forEach((location, index) => {
    const city = normalizeCity(location?.details?.city || "Unknown");
    const state = location?.details?.state;
    const phone =
      location?.details?.storeOwnerPhoneNumber || `__no_phone__${index}`;
    const coords = location?.locationCoordinates || {};
    const product = location?.product || {};
    const productName = product?.productType || "Unknown";

    // area (ensure numeric)
    const productAreaSqFt =
      (Number(product?.productHeight) || 0) *
      (Number(product?.productWidth) || 0);

    // If a productType filter is provided and this location is not that product, skip it.
    if (productType && productName !== productType) return;

    const formattedLocation = {
      id: index + 1,
      locationCoordinates: {
        latitude: coords.latitude ?? coords.lat,
        longitude: coords.longitude ?? coords.lng,
      },
      product,
    };

    if (!cityMap[city]) {
      cityMap[city] = {
        sites: 0,
        state,
        locations: new Map(),
        totalAreaSqFt: 0,
      };
    }

    const entry = cityMap[city];

    // count a site once per unique phone
    if (!entry.locations.has(phone)) {
      entry.locations.set(phone, formattedLocation);
      entry.sites += 1;
    }

    // accumulate area
    entry.totalAreaSqFt += productAreaSqFt;
  });
  // Convert to array
  return Object.entries(cityMap).map(([name, value]) => ({
    name,
    sites: value.sites,
    locations: Array.from(value.locations.values()),
    state: value.state,
    // NOTE: when productType was passed, totalAreaSqFt here equals total for that product only
    totalAreaSqFt: value.totalAreaSqFt,
  }));
};

const fetchAllVendorPartners = async (city) => {
  const serverURL = process.env.REACT_APP_URL;
  try {
    const url = city
      ? `${serverURL}/api/vendorsForMap?city=${encodeURIComponent(city)}`
      : `${serverURL}/api/vendorsForMap`;

    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    if (response?.data?.success) {
      return response.data.data;
    }
  } catch (error) {
    console.error("Error fetching InstallationPartner:", error);
  }
};

const normalizeCoord = (coord) => {
  if (!coord) return null;

  const lat = Number(coord.latitude ?? coord.lat);
  const lng = Number(coord.longitude ?? coord.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return { latitude: lat, longitude: lng };
};

const toRadians = (deg) => (deg * Math.PI) / 180;

const haversineDistance = (c1, c2) => {
  if (!c1 || !c2) return NaN;
  const lat1 = c1.latitude;
  const lon1 = c1.longitude;
  const lat2 = c2.latitude;
  const lon2 = c2.longitude;

  if ([lat1, lon1, lat2, lon2].some((v) => Number.isNaN(v))) return NaN;

  const R = 6371; // km
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
    Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const calculateRouteDistance = (startCoordInput, path = []) => {
  const startCoord = normalizeCoord(startCoordInput);
  if (!startCoord || !path || path.length === 0) return 0;

  let total = 0;
  let current = startCoord;

  for (const loc of path) {
    const next = normalizeCoord(loc.locationCoordinates);
    if (!next) continue; // skip invalid location
    const d = haversineDistance(current, next);
    if (!Number.isNaN(d)) total += d;
    current = next;
  }

  // return to start if possible
  const back = haversineDistance(current, startCoord);
  if (!Number.isNaN(back)) total += back;

  return total; // in km (number)
};

// --- Genetic Algorithm Core ---

function geneticTSP(IP, locations, options = {}) {
  const populationSize = options.populationSize || 50;
  const generations = options.generations || 100;
  const mutationRate = options.mutationRate || 0.1;

  // Create initial population (random permutations)
  const createPopulation = () => {
    const pop = [];
    for (let i = 0; i < populationSize; i++) {
      pop.push(shuffle([...locations]));
    }
    return pop;
  };

  const shuffle = (arr) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // Fitness: lower distance = better
  const evaluateFitness = (individual) =>
    calculateRouteDistance(IP.locationCoordinates, individual);

  // Selection: pick top 20% individuals
  const selectParents = (population) => {
    const sorted = [...population].sort(
      (a, b) => evaluateFitness(a) - evaluateFitness(b),
    );
    return sorted.slice(0, Math.floor(populationSize * 0.2));
  };

  // Crossover: ordered crossover
  const crossover = (parent1, parent2) => {
    const start = Math.floor(Math.random() * parent1.length);
    const end = start + Math.floor(Math.random() * (parent1.length - start));

    const slice = parent1.slice(start, end);
    const remaining = parent2.filter((p) => !slice.includes(p));
    return [...slice, ...remaining];
  };

  // Mutation: swap two random locations
  const mutate = (individual) => {
    if (Math.random() > mutationRate) return individual;
    const idx1 = Math.floor(Math.random() * individual.length);
    const idx2 = Math.floor(Math.random() * individual.length);
    [individual[idx1], individual[idx2]] = [individual[idx2], individual[idx1]];
    return individual;
  };

  // Main loop
  let population = createPopulation();
  let bestIndividual = population[0];
  let bestDistance = evaluateFitness(bestIndividual);

  for (let gen = 0; gen < generations; gen++) {
    const parents = selectParents(population);
    const newPop = [];

    while (newPop.length < populationSize) {
      const p1 = parents[Math.floor(Math.random() * parents.length)];
      const p2 = parents[Math.floor(Math.random() * parents.length)];
      let child = crossover(p1, p2);
      child = mutate(child);
      newPop.push(child);
    }

    population = newPop;

    for (const ind of population) {
      const dist = evaluateFitness(ind);
      if (dist < bestDistance) {
        bestDistance = dist;
        bestIndividual = ind;
      }
    }
  }

  return {
    totalDistanceKm: Number(bestDistance.toFixed(2)),
    optimizedPath: [IP, ...bestIndividual, IP], // return path including return to base
  };
}

// calculate centroid :
const calculateCentroid = (locations = []) => {
  const coords = locations
    .map((l) => normalizeCoord(l.locationCoordinates))
    .filter(Boolean);

  if (!coords.length) return null;

  const sum = coords.reduce(
    (acc, c) => {
      acc.latitude += c.latitude;
      acc.longitude += c.longitude;
      return acc;
    },
    { latitude: 0, longitude: 0 },
  );

  return {
    latitude: sum.latitude / coords.length,
    longitude: sum.longitude / coords.length,
  };
};

function getDistance(lat1, lon1, lat2, lon2) {
  const toRad = (val) => (val * Math.PI) / 180;
  const R = 6371; // Earth radius in KM

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
